import { useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Paper, Stack, TextField, Button, Grow } from '@mui/material';
import { styled } from '@mui/system';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import toast from 'react-hot-toast';
import { BsFillBookmarkCheckFill } from "react-icons/bs";
import axios from 'axios';
import { MdOutlineArrowForwardIos } from "react-icons/md";
import { NCRTooltip } from '../../../Utilities/NCRFormHeading';
import { getHeaders } from '../../../../Helpers/NCRHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { setFormData } from '../../../../Helpers/actionFormSlice';
import { useNavigate } from 'react-router-dom';
import { setTab } from '../../../../Helpers/helperSlice';
import { CheckCircle } from '@mui/icons-material';


export const NavButton = ({ icon, title, onClick, disabled, isSubmitted }) => (
    <NCRTooltip title={(!disabled && onClick) && title}>
        <div
            onClick={() => (!disabled && onClick) && onClick()}
            style={{
                cursor: (!disabled && onClick) && "pointer",
                color: disabled ? 'gray' : (title === "Submit" && isSubmitted) ? '#c0392b' : '#283747',
            }}
        >
            {icon}
        </div>
    </NCRTooltip>
);

function hexToRgba(hex, alpha) {
    const r = parseInt(hex?.slice(1, 3), 16);
    const g = parseInt(hex?.slice(3, 5), 16);
    const b = parseInt(hex?.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const StyledBox = styled(Paper)(({ bgcolor, idx }) => ({
    border: `5px solid ${bgcolor}`,
    padding: '20px',
    borderRadius: '10px',
    marginLeft: idx === 0 || idx === 4 ? '3px' : '5px',
    minWidth: '100%',
    minHeight: '150px',
    maxHeight: 'auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#000',
    position: 'relative',
    background: `linear-gradient(to bottom right, ${hexToRgba(bgcolor, 0.2)}, rgba(240, 240, 240, 0.4))`,
}));

const defaultData = [
    { id: 1, title: 'Why 1', content: '', color: '#FFD700', is_submitted: false },
    { id: 2, title: 'Why 2', content: '', color: '#9ACD32', is_submitted: false },
    { id: 3, title: 'Why 3', content: '', color: '#32CD32', is_submitted: false },
    { id: 4, title: 'Why 4', content: '', color: '#00BFFF', is_submitted: false },
    { id: 5, title: 'Why 5', content: '', color: '#1E90FF', is_submitted: false }
];

export default function WhysFlowPage({ whyAnalysisState }) {
    const { formData, whyData, setWhyData, preWhyData, setPreWhyData, current, setCurrent, isWhyEditBtn, setIsWhyEditBtn } = whyAnalysisState;

    const dispatch = useDispatch();
    const actionFormData = useSelector((state) => state.actionForm.formData);
    const navigate = useNavigate();

    const toastIdRef = useRef(null);
    useEffect(() => {
        return () => {
            if (toastIdRef.current) {
                toast.dismiss(toastIdRef.current);
                toastIdRef.current = null;
            }
        };
    }, []);

    const handleNext = () => {
        if (whyData.length >= defaultData.length) return;

        const newItem = {
            id: whyData.length,
            ...defaultData[whyData.length],
        };

        const updatedData = [...whyData, newItem];
        setWhyData(updatedData);

        if (current < updatedData.length - 1) setCurrent(current + 1);
    };

    const handlePrevious = () => {
        setWhyData(prev => prev.slice(0, current))

        if (current > 0) setCurrent(current - 1);
    };

    const handleChange = (index, value) => {
        if (value?.length > 250) return toast.error("Maximum 250 characters allowed");
        const updatedData = structuredClone(whyData);
        updatedData[index].content = value;
        setWhyData(updatedData);
    };

    const handleSubmit = async (index) => {
        const updatedData = structuredClone(whyData);
        if (!updatedData[index]?.content?.trim() && updatedData[index].is_submitted === false) {
            toast.error("Please fill the field before submitting");
        } else {
            updatedData[index].is_submitted = !updatedData[index].is_submitted;
        }
        setWhyData(updatedData);
    };

    const submitWhyWhyData = async (ncrId = '', payload = {}) => {
        try {
            const response = await axios.put(`${process.env.REACT_APP_BACKEND_BASE_URL}/whywhy/${ncrId}`, payload, getHeaders());
            const value = response?.data?.data?.find((e) => e.is_submitted === true)
            dispatch(setFormData({
                ...actionFormData,
                process_root_cause_description: value?.content || '',
            }))

            setPreWhyData(whyData);
            if (toastIdRef.current) return response?.data?.data || [];
            toastIdRef.current = toast.custom((t) => (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                        padding: '10px 15px',
                        gap: '10px',
                        color: '#333',
                    }}
                >
                    <CheckCircle style={{ color: '#22c55e' }} />
                    <div style={{ display: "flex", gap: "10px" }}>
                        <span>Why Why analysis added to 8D Report Successfully</span>
                        <span
                            style={{ color: '#2A3DBD', cursor: "pointer", fontWeight: 500 }}
                            onClick={() => {
                                toast.dismiss(t.id);
                                toastIdRef.current = null;
                                dispatch(setTab(1));
                                navigate(`/ncpro/action/${formData?.ncr_id}`, {
                                    state: { fromRC: true, ncr_id: formData?.ncr_id, isScroll: true }
                                });
                            }}
                        >
                            Click here to view NC
                        </span>
                    </div>
                </div>
            ), { duration: Infinity });
            return response?.data?.data || [];
        } catch (error) {
            toast.error(error?.message || 'Submission failed');
            return [];
        }
    };

    const handleApiCall = async () => {
        const resData = await submitWhyWhyData(formData?.ncr_id, whyData);
        if (resData.length > 0) {
            setWhyData(resData);
            setIsWhyEditBtn(true);
        }
    };

    const resetForm = useCallback(() => {
        setWhyData(preWhyData);
        setCurrent(preWhyData.length - 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preWhyData]);

    return (
        <Box>
            <Grow
                in={true}
                style={{ transformOrigin: '0 0 0' }}
                timeout={1000}
            >
                <Box sx={{ p: 4, py: 8, margin: 'auto' }}>
                    <Typography sx={{ fontFamily: 'Lato, sans-serif', fontSize: "16px", margin: '0 auto' }}>
                        <span style={{ fontWeight: 600 }}> Note :</span> Conduct a 'Why-Why' analysis using the identified causes, then click the submit button to confirm the final root cause.
                    </Typography>
                    <Stack direction={{ xs: "column", md: "row" }} rowGap={5} alignItems="flex-start" style={{ marginTop: "40px" }}>
                        {whyData?.map((item, index) => {
                            const textFiledStyles = item?.is_submitted ? {
                                minHeight: "100px",
                                backgroundColor: "#c0392b",
                                color: "white",
                                display: "flex",
                                alignItems: "start",
                                flexWrap: "wrap",
                                borderRadius: "8px",
                            } : {}
                            return (
                                <div key={item?.id} style={{ width: '265px' }}>
                                    <Grow
                                        in={index + 1}
                                        style={{ transformOrigin: 'left center' }} // or '0% 50%'
                                        timeout={1000}
                                    >
                                        <div style={{ width: "100%" }}>
                                            <div style={{
                                                width: '100%',
                                                display: 'flex',
                                                justifyContent: "start",
                                                alignItems: "center",
                                            }}>
                                                <div style={{
                                                    border: '3px solid rgba(128, 128, 128, 0.2)',
                                                    width: '65%',
                                                    display: 'flex',
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    borderRadius: "10%",
                                                    padding: '8px 10px 8px 5px'
                                                }}>
                                                    <StyledBox bgcolor={item?.color} idx={index} style={{ width: "100%" }}>
                                                        <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'Lato, sans-serif', }}>
                                                            {item?.title}
                                                        </Typography>
                                                    </StyledBox>
                                                </div>
                                                {index < current && (
                                                    <div style={{
                                                        width: '35%',
                                                        paddingLeft: "5px",
                                                        height: "3px",
                                                        backgroundColor: "#e5e7e9",
                                                        position: "relative"
                                                    }}>
                                                        <div style={{ position: "absolute", transform: 'translate(-50%,-50%)', left: "45%", top: "5px" }}>
                                                            <MdOutlineArrowForwardIos style={{ fontSize: 32, color: item?.color, marginLeft: "12px", fontWeight: 900 }} />
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div style={{
                                                width: '60%',
                                                display: 'flex',
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                gap: "10px",
                                                marginTop: '10px',
                                                marginLeft: "2px"
                                            }}>
                                                {index < 4 && (
                                                    <NavButton
                                                        title="Next"
                                                        icon={<SkipNextIcon style={{ fontSize: "25px" }} />}
                                                        onClick={handleNext}
                                                        disabled={!item?.content || (current !== index) || item?.is_submitted || isWhyEditBtn}
                                                    />
                                                )}

                                                {index !== 0 && (
                                                    <NavButton
                                                        title="Previous"
                                                        icon={<SkipPreviousIcon style={{ fontSize: "25px" }} />}
                                                        onClick={handlePrevious}
                                                        disabled={(current !== index) || item?.is_submitted || isWhyEditBtn}
                                                    />
                                                )}

                                                <NavButton
                                                    title="Submit"
                                                    icon={<BsFillBookmarkCheckFill style={{ fontSize: "22px" }} />}
                                                    onClick={() => handleSubmit(index)}
                                                    disabled={!item?.content || (current !== index) || isWhyEditBtn}
                                                    isSubmitted={item?.is_submitted}
                                                />
                                            </div>

                                            <div style={{
                                                width: "63%",
                                                marginTop: "10px",
                                                minHeight: '40px',
                                                borderRadius: "5px",
                                                display: "flex",
                                                alignItems: "center"
                                            }}>
                                                {index === current ?
                                                    <div>
                                                        <TextField
                                                            value={item?.content}
                                                            onChange={(e) => handleChange(index, e.target.value)}
                                                            InputProps={{
                                                                readOnly: item?.is_submitted,
                                                                sx: {
                                                                    fontFamily: 'Lato, sans-serif',
                                                                    minHeight: '40px',
                                                                    padding: '8px 14px',
                                                                    fontSize: '14px',
                                                                    width: '100%',
                                                                    ...textFiledStyles,
                                                                },
                                                                inputProps: {
                                                                    'aria-readonly': item?.is_submitted || false,
                                                                },
                                                            }}
                                                            autoFocus={(!item?.is_submitted && index === current)}
                                                            aria-readonly={item?.is_submitted}
                                                            multiline
                                                            fullWidth
                                                            placeholder='Enter value here...'
                                                        />
                                                        {item?.is_submitted && <Typography style={{
                                                            fontFamily: 'Lato, sans-serif',
                                                            width: "100%",
                                                            wordBreak: "break-word",
                                                            whiteSpace: "normal",
                                                            fontSize: "15px",
                                                            marginTop: "10px",
                                                            textAlign: "center",
                                                            fontWeight: 400
                                                        }}>
                                                            FINAL ROOT CAUSE
                                                        </Typography>
                                                        }
                                                    </div>
                                                    :
                                                    <div style={{ width: "100%" }}>
                                                        <div style={{
                                                            width: "100%",
                                                            minHeight: "100px",
                                                            backgroundColor: "rgba(128, 128, 128, 0.2)",
                                                            display: "flex",
                                                            justifyContent: "flex-start",
                                                            flexWrap: "wrap",
                                                            borderRadius: "8px",
                                                            padding: "10px",
                                                            ...textFiledStyles
                                                        }}>
                                                            <Typography style={{
                                                                fontFamily: 'Lato, sans-serif',
                                                                width: "100%",
                                                                wordBreak: "break-word",
                                                                whiteSpace: "normal",
                                                                fontSize: "15px",
                                                            }}>
                                                                {item?.content}
                                                            </Typography>
                                                        </div>
                                                        {item?.is_submitted && <Typography style={{
                                                            fontFamily: 'Lato, sans-serif',
                                                            width: "100%",
                                                            wordBreak: "break-word",
                                                            whiteSpace: "normal",
                                                            fontSize: "15px",
                                                            marginTop: "10px",
                                                            textAlign: "center",
                                                            fontWeight: 400
                                                        }}>
                                                            FINAL ROOT CAUSE
                                                        </Typography>
                                                        }
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    </Grow>
                                </div>
                            )
                        })}
                    </Stack>

                    {whyData?.some(item => item?.is_submitted) && <Typography sx={{ fontFamily: 'Lato, sans-serif', display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", marginY: '40px', fontWeight: 600 }}>
                        <span style={{ color: "red", fontSize: "20px" }}>*</span> THIS FINAL ROOT CAUSE HAS BEEN LINKED TO PROCESS ROOT CAUSE DESCRIPTION FIELD
                    </Typography>}
                </Box>
            </Grow>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                px: 3, py: 2,
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                zIndex: 10,
                borderRadius: '0px 0px 10px 10px',
                boxShadow: '0px -1px 4px 0px rgba(0, 0, 0, 0.15)'
            }}>
                {isWhyEditBtn ?
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            color="neutral"
                            sx={{
                                background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                                textTransform: 'none',
                                fontFamily: 'Lato, sans-serif',
                                fontSize: '14px',
                                fontWeight: '700',
                                color: 'rgba(255, 255, 255, 1) !important',
                                borderRadius: '10px',
                                border: 'none !important',
                                p: 1,
                                px: 2,
                            }}
                            onClick={() => setIsWhyEditBtn(false)}
                        >
                            Edit
                        </Button>
                    </Box>
                    :
                    <>
                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                color="neutral"
                                sx={{
                                    fontFamily: 'Lato, sans-serif',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                    border: '1px solid rgba(14, 41, 102, 1) !important',
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    color: 'rgba(14, 41, 102, 1) !important',
                                    py: 1
                                }}
                                onClick={() => resetForm()}
                            >
                                Reset
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                color="neutral"
                                sx={{
                                    background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                                    textTransform: 'none',
                                    fontFamily: 'Lato, sans-serif',
                                    fontSize: '14px',
                                    fontWeight: '700',
                                    color: 'rgba(255, 255, 255, 1) !important',
                                    borderRadius: '10px',
                                    border: 'none !important',
                                    p: 1,
                                    px: 2,
                                    opacity: !whyData?.some(item => item?.is_submitted) ? 0.7 : 1
                                }}
                                disabled={!whyData?.some(item => item?.is_submitted)}
                                onClick={() => handleApiCall()}
                            >
                                Submit
                            </Button>
                        </Box>
                    </>}
            </Box>
        </Box>
    );
}
